'use client';

import { useCallback, useMemo, useRef, useState } from 'react';
import { useDebounce, useInterval } from 'react-use';
import { SwapQuote, UnifiedSwapClient } from '@velocity-exchange/sdk';
import { PublicKey } from '@solana/web3.js';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import {
	createSwapClient,
	getQuoteSwapClient,
	isValidQuote,
} from 'src/utils/swap';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';
import {
	SWAP_AND_DEPOSIT_SLIPPAGE_BPS,
	USDC_MAINNET_MINT,
	USDC_PRECISION_EXP,
} from 'src/constants/swapAndDeposit';

const QUOTE_DEBOUNCE_MS = 300;
const QUOTE_REFRESH_MS = 15_000;

/**
 * Jupiter-only quote for the fixed USDC → USDT pair. Jupiter is forced
 * (decision on FE-4422) so display and execution can't disagree with the
 * user's configured Titan/Jupiter setting.
 */
export const useUsdcToUsdtQuote = ({
	usdcAmount,
	usdtMint,
	enabled,
}: {
	/** Raw input string from the From field. */
	usdcAmount: string;
	usdtMint: PublicKey | undefined;
	enabled: boolean;
}) => {
	const connection = useVelocityStore((s) => s.connection.current);
	const authority = useVelocityStore(
		(s) => s.wallet.current.adapter?.publicKey
	);

	const [quote, setQuote] = useState<SwapQuote | null>(null);
	const [isQuoteLoading, setIsQuoteLoading] = useState(false);
	// Client that produced `quote` — routes only build on the provider that
	// quoted them, and v2 quotes are bound to their taker.
	const quoteClientRef = useRef<UnifiedSwapClient | null>(null);
	// Guards against a stale response overwriting a newer one.
	const requestIdRef = useRef(0);

	const jupiterClient = useMemo(
		() =>
			connection
				? createSwapClient({ clientType: 'jupiter', connection })
				: null,
		[connection]
	);

	// Anything the field cannot be read as a number reads as zero, which stops
	// the quote below on `gtZero`.
	const amountBigNum = useMemo(
		() => parseInputBigNumOrZero(usdcAmount || '0', USDC_PRECISION_EXP),
		[usdcAmount]
	);

	const fetchQuote = useCallback(async () => {
		const requestId = ++requestIdRef.current;

		if (
			!enabled ||
			!jupiterClient ||
			!connection ||
			!authority ||
			!usdtMint ||
			!amountBigNum.gtZero()
		) {
			setQuote(null);
			quoteClientRef.current = null;
			setIsQuoteLoading(false);
			return;
		}

		setIsQuoteLoading(true);
		try {
			const quoteClient = getQuoteSwapClient({
				swapClient: jupiterClient,
				connection,
				swapMode: 'ExactIn',
				autoSlippage: false,
				userPublicKey: authority,
			});

			const newQuote = await quoteClient.getQuote({
				inputMint: USDC_MAINNET_MINT,
				outputMint: usdtMint,
				amount: amountBigNum.val,
				userPublicKey: authority,
				swapMode: 'ExactIn',
				slippageBps: SWAP_AND_DEPOSIT_SLIPPAGE_BPS,
				autoSlippage: false,
			});

			if (requestId !== requestIdRef.current) return; // stale response

			if (isValidQuote(newQuote)) {
				setQuote(newQuote);
				quoteClientRef.current = quoteClient;
			} else {
				setQuote(null);
				quoteClientRef.current = null;
			}
		} catch (err) {
			console.error('Error fetching USDC→USDT quote', err);
			if (requestId !== requestIdRef.current) return;
			setQuote(null);
			quoteClientRef.current = null;
		} finally {
			if (requestId === requestIdRef.current) {
				setIsQuoteLoading(false);
			}
		}
	}, [enabled, jupiterClient, connection, authority, usdtMint, amountBigNum]);

	// Debounce on input changes…
	useDebounce(fetchQuote, QUOTE_DEBOUNCE_MS, [fetchQuote]);

	// …and keep the quote fresh while it's displayed. `react-use`'s
	// `useDebounce` never runs cleanup functions returned by its callback, so
	// an interval started that way would leak — `useInterval` is the correct
	// primitive for a recurring poll.
	useInterval(fetchQuote, quote ? QUOTE_REFRESH_MS : null);

	return { quote, quoteClient: quoteClientRef.current, isQuoteLoading };
};
